import { useMemo, useState } from "react";
import {
  Search,
  Filter,
  ArrowUpDown,
  ArrowDownUp,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Layers,
  Coins,
  DollarSign,
  TrendingUp,
  BarChart2,
  Pencil,
  Trash2,
  Zap,
  MoreHorizontal,
  Eye,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import type { Package } from "../../types/package";
import { useAuth } from "../../contexts/AuthContext";

type StatusFilter = "all" | "published" | "draft";
type ActivityFilter = "all" | "active" | "inactive";
type SortField = "createdAt" | "price" | "purchaseCount" | "title";
type SortOrder = "asc" | "desc";

interface PackageTableProps {
  packages: Package[];
  isLoading?: boolean;
  onAddClick: () => void;
  onEditClick: (pkg: Package) => void;
  onDeleteClick: (pkg: Package) => void;
  onTogglePublish: (pkg: Package) => void;
  onToggleActive: (pkg: Package) => void;
  onViewStats: (pkg: Package) => void;
  onReorderClick?: () => void;
}

const formatCurrency = (value?: number) => {
  if (value == null) return "-";
  return `$${value.toFixed(2)}`;
};

export function PackageTable({
  packages,
  isLoading,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onTogglePublish,
  onToggleActive,
  onViewStats,
  onReorderClick,
}: PackageTableProps) {
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('packages:create');
  const canUpdate = hasPermission('packages:update');
  const canDelete = hasPermission('packages:delete');
  const canViewStats = hasPermission('packages:view-stats');

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  const filteredPackages = useMemo(() => {
    return packages
      .filter((pkg) => {
        const matchesSearch =
          pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pkg.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pkg.tags?.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase()),
          );

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "published" ? pkg.isPublished : !pkg.isPublished);

        const matchesActivity =
          activityFilter === "all" ||
          (activityFilter === "active" ? pkg.isActive : !pkg.isActive);

        return matchesSearch && matchesStatus && matchesActivity;
      })
      .sort((a, b) => {
        let valA: string | number = "";
        let valB: string | number = "";

        switch (sortField) {
          case "title":
            valA = a.title.toLowerCase();
            valB = b.title.toLowerCase();
            break;
          case "price":
            valA = a.price;
            valB = b.price;
            break;
          case "purchaseCount":
            valA = a.purchaseCount || 0;
            valB = b.purchaseCount || 0;
            break;
          case "createdAt":
          default:
            valA = new Date(a.createdAt).getTime();
            valB = new Date(b.createdAt).getTime();
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
  }, [
    packages,
    searchTerm,
    statusFilter,
    activityFilter,
    sortField,
    sortOrder,
  ]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setIsSortOpen(false);
  };

  const renderStatusBadge = (pkg: Package) => {
    if (!pkg.isActive) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
          <PauseCircle size={12} />
          Inactive
        </span>
      );
    }

    if (pkg.isPublished) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          <CheckCircle2 size={12} />
          Published
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">
        <XCircle size={12} />
        Draft
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Packages</h2>
          <p className="text-sm text-gray-500">
            Bundle multiple courses, manage pricing, and track performance.
          </p>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full xl:w-[408px]">
          <Input
            placeholder="Search packages..."
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filters
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-3 min-w-[240px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Publish Status
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        { label: "All", value: "all" },
                        { label: "Published", value: "published" },
                        { label: "Draft", value: "draft" },
                      ].map((filter) => (
                        <button
                          key={filter.value}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            statusFilter === filter.value
                              ? "bg-indigo-600 text-white"
                              : "bg-gray-100 text-gray-700"
                          }`}
                          onClick={() => {
                            setStatusFilter(filter.value as StatusFilter);
                          }}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>

                    <p className="mt-4 text-xs font-semibold uppercase text-gray-500">
                      Activity
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" },
                      ].map((filter) => (
                        <button
                          key={filter.value}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            activityFilter === filter.value
                              ? "bg-indigo-600 text-white"
                              : "bg-gray-100 text-gray-700"
                          }`}
                          onClick={() => {
                            setActivityFilter(filter.value as ActivityFilter);
                          }}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px] animate-in fade-in zoom-in duration-150">
                    {[
                      { label: "Newest", value: "createdAt" },
                      { label: "Title", value: "title" },
                      { label: "Price", value: "price" },
                      { label: "Purchases", value: "purchaseCount" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        className={`w-full rounded px-3 py-2 text-left text-sm ${
                          sortField === option.value
                            ? "bg-gray-100 font-semibold text-indigo-600"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => handleSort(option.value as SortField)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {onReorderClick && (
            <Button
              variant="outline"
              onClick={onReorderClick}
              className="flex items-center gap-2"
            >
              <ArrowDownUp size={16} />
              Reorder
            </Button>
          )}

          {canCreate && (
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-[180px] whitespace-nowrap h-[48px] sm:h-[44px] touch-target"
              onClick={onAddClick}
            >
              Create package
            </Button>
          )}
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50/80 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left">Package</th>
                <th className="px-3 sm:px-6 py-3 text-left">Courses</th>
                <th className="px-3 sm:px-6 py-3 text-left">Pricing</th>
                <th className="px-3 sm:px-6 py-3 text-left">Performance</th>
                <th className="px-3 sm:px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 sm:px-6 py-12 text-center text-gray-500"
                  >
                    Loading packages...
                  </td>
                </tr>
              ) : filteredPackages.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 sm:px-6 py-12 text-center text-gray-500"
                  >
                    No packages match the current filters.
                  </td>
                </tr>
              ) : (
                filteredPackages.map((pkg) => (
                  <tr key={pkg._id} className="align-top">
                    <td className="px-3 sm:px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            {pkg.title}
                          </p>
                          {renderStatusBadge(pkg)}
                        </div>
                        <p className="line-clamp-2 text-xs text-gray-500">
                          {pkg.description}
                        </p>
                        {pkg.tags && pkg.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {pkg.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600"
                              >
                                {tag}
                              </span>
                            ))}
                            {pkg.tags.length > 3 && (
                              <span className="text-xs text-gray-400">
                                +{pkg.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="space-y-1 text-sm text-gray-700">
                        <div className="flex items-center gap-2 font-semibold">
                          <Layers size={14} className="text-indigo-500" />
                          {pkg.totalCourses} courses
                        </div>
                        <ul className="list-disc pl-5 text-xs text-gray-500">
                          {pkg.courses.slice(0, 3).map((course) => (
                            <li key={course._id}>{course.title}</li>
                          ))}
                        </ul>
                        {pkg.courses.length > 3 && (
                          <p className="text-xs text-gray-400">
                            +{pkg.courses.length - 3} more
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-gray-900">
                          <Coins size={14} className="text-amber-500" />
                          <span className="text-base font-semibold">
                            {formatCurrency(pkg.price)} {pkg.currency}
                          </span>
                        </div>
                        {pkg.originalPrice && pkg.originalPrice > pkg.price && (
                          <p className="text-xs text-gray-500">
                            <span className="line-through opacity-70">
                              {formatCurrency(pkg.originalPrice)}
                            </span>{" "}
                            <span className="text-emerald-600 font-semibold">
                              -{pkg.discountPercentage ?? 0}%
                            </span>
                          </p>
                        )}
                        {pkg.validityPeriod && (
                          <p className="text-xs text-gray-500">
                            Access: {pkg.validityPeriod} days
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="space-y-2 text-xs text-gray-500">
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <TrendingUp size={14} className="text-blue-500" />
                          {pkg.purchaseCount || 0} purchases
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <DollarSign size={14} className="text-green-500" />
                          {formatCurrency(pkg.totalRevenue || 0)} total
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="px-2 text-xs text-indigo-600"
                          onClick={() => onViewStats(pkg)}
                        >
                          <BarChart2 size={14} />
                          View stats
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right">
                      <div className="flex justify-end relative">
                        <button
                          className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeMenu?.id === pkg._id) {
                              setActiveMenu(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setActiveMenu({
                                id: pkg._id,
                                position: {
                                  top: rect.bottom,
                                  right: window.innerWidth - rect.right,
                                },
                              });
                            }
                          }}
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        <TableActionMenu
                          isOpen={activeMenu?.id === pkg._id}
                          onClose={() => setActiveMenu(null)}
                          position={activeMenu?.position || null}
                        >
                          {canViewStats && (
                            <button
                              onClick={() => {
                                onViewStats(pkg);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <Eye size={14} className="text-gray-500" />
                              View Stats
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              onClick={() => {
                                onTogglePublish(pkg);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              {pkg.isPublished ? (
                                <>
                                  <Zap size={14} className="text-yellow-500" />
                                  Unpublish
                                </>
                              ) : (
                                <>
                                  <Zap size={14} className="text-gray-400" />
                                  Publish
                                </>
                              )}
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              onClick={() => {
                                onToggleActive(pkg);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              {pkg.isActive ? (
                                <>
                                  <PauseCircle
                                    size={14}
                                    className="text-yellow-500"
                                  />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle2
                                    size={14}
                                    className="text-green-500"
                                  />
                                  Activate
                                </>
                              )}
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              onClick={() => {
                                onEditClick(pkg);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <Pencil size={14} className="text-blue-500" />
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => {
                                onDeleteClick(pkg);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </TableActionMenu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>Showing {filteredPackages.length} packages</span>
        </div>
      </Card>
    </div>
  );
}
